import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ImageLightbox from "@/components/base/ImageLightbox";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { formatCount, formatDate } from "@/utils/format";

function ScoreRing({ score }: { score: number }) {
  const percent = Math.min(100, (score / 10) * 100);
  return (
    <div className="relative w-20 h-20 md:w-24 md:h-24 shrink-0">
      <div
        className="w-full h-full rounded-full"
        style={{
          background: `conic-gradient(oklch(var(--primary-500)) ${percent}%, oklch(var(--background-300)) ${percent}% 100%)`,
        }}
      />
      <div className="absolute inset-[6px] rounded-full bg-background-50 flex flex-col items-center justify-center">
        <span className="font-heading font-bold text-foreground-950 text-xl md:text-2xl leading-none">
          {score.toFixed(1)}
        </span>
        <span className="mt-0.5 text-[10px] text-foreground-500 font-label">综 合 评 分</span>
      </div>
    </div>
  );
}

export default function AnimeDetail() {
  const { id } = useParams();
  const anime = animeList.find((a) => a.id === id);
  const [preview, setPreview] = useState<string | null>(null);

  if (!anime) {
    return (
      <SiteLayout
        title="作品未找到 - 91动漫"
        description="该动漫作品信息可能已被移除，可返回动漫作品分类页浏览其他动画。"
        keywords="动漫作品,91动漫"
      >
        <section className="w-full px-4 md:px-8 py-16 md:py-24 bg-background-50">
          <EmptyState
            icon="ri-movie-line"
            title="作品信息未找到"
            desc="你访问的作品可能已下架或链接有误。可以回到作品库继续浏览。"
            actionLabel="返回作品列表"
            actionTo="/anime"
          />
        </section>
      </SiteLayout>
    );
  }

  const related = animeList
    .filter((a) => a.id !== anime.id)
    .sort((a, b) => {
      const aOverlap = a.genres.filter((g) => anime.genres.includes(g)).length;
      const bOverlap = b.genres.filter((g) => anime.genres.includes(g)).length;
      if (aOverlap !== bOverlap) return bOverlap - aOverlap;
      return b.rating - a.rating;
    })
    .slice(0, 4);

  const relatedNews = newsList.filter((n) => n.relatedAnimeIds.includes(anime.id)).slice(0, 3);

  return (
    <SiteLayout
      title={`${anime.title} - 91动漫`}
      description={`${anime.title}（${anime.jpTitle}）：${anime.summary.slice(0, 60)} 类型：${anime.genres.join("、")}，${anime.year} 年 ${anime.region}动画。`}
      keywords={`${anime.title},${anime.genres.join(",")},${anime.region}动漫,动漫作品介绍,91动漫`}
    >
      <section className="relative w-full bg-foreground-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={anime.banner}
            alt={`${anime.title} 场景图`}
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/85 to-foreground-950/70" />
        </div>

        <div className="relative w-full px-4 md:px-8 pt-6 md:pt-8 pb-8 md:pb-12">
          <div className="text-background-300">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "动漫作品", to: "/anime" },
                { label: anime.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-5 md:gap-8">
            <button
              type="button"
              onClick={() => setPreview(anime.cover)}
              className="w-40 md:w-[220px] shrink-0 rounded-lg overflow-hidden bg-background-800 cursor-zoom-in group self-start"
              aria-label="放大查看封面"
            >
              <img
                src={anime.cover}
                alt={`${anime.title} 封面`}
                title={`${anime.title} 动漫作品封面`}
                className="w-full aspect-[3/4] object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.04]"
              />
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] font-label">
                  {anime.status}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-200 text-[11px] font-label">
                  {anime.region}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/10 text-background-200 text-[11px] font-label">
                  {anime.year}
                </span>
              </div>
              <h1 className="mt-3 font-heading font-bold text-background-50 text-2xl md:text-4xl tracking-tight">
                {anime.title}
              </h1>
              <p className="mt-1 text-xs md:text-sm text-background-400 font-label">{anime.jpTitle}</p>
              <p className="mt-3 text-sm md:text-base text-background-200 leading-relaxed max-w-2xl">
                {anime.summary}
              </p>

              <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 max-w-2xl">
                {[
                  { label: "动画类型", value: anime.genres.join(" / ") },
                  { label: "集数", value: anime.episodes },
                  { label: "制作公司", value: anime.studio },
                  { label: "更新时间", value: formatDate(anime.updatedAt) },
                  { label: "热度", value: `${formatCount(anime.heat)} 次讨论` },
                  { label: "地区", value: anime.region },
                ].map((row) => (
                  <div key={row.label}>
                    <dt className="text-[11px] text-background-400 font-label">{row.label}</dt>
                    <dd className="mt-0.5 text-xs md:text-sm text-background-100">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap gap-2">
                {anime.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-background-50/10 text-background-200 text-[11px] font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-12">
          <div className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <ScoreRing score={anime.rating} />
              <div className="flex-1">
                <h2 className="font-heading font-semibold text-foreground-950 text-base md:text-lg">编辑推荐理由</h2>
                <ul className="mt-2 space-y-1.5">
                  {anime.reasons.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-xs md:text-sm text-foreground-700 leading-relaxed">
                      <span className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0 text-primary-600">
                        <i className="ri-check-double-line text-sm" />
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl flex items-center gap-2.5">
                <span className="w-1.5 h-4 rounded-full bg-primary-500" />
                作品简介
              </h2>
              <div className="mt-4 space-y-3.5">
                {anime.intro.map((p, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-[1.95]">
                    {p}
                  </p>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl flex items-center gap-2.5">
                <span className="w-1.5 h-4 rounded-full bg-accent-500" />
                主要角色
              </h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {anime.characters.map((c) => (
                  <div key={c.name} className="p-4 rounded-lg bg-background-100 border border-background-200">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center font-heading font-bold text-sm">
                        {c.name.slice(0, 1)}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{c.name}</p>
                        <p className="text-[11px] text-foreground-500 font-label">CV. {c.cv}</p>
                      </div>
                    </div>
                    <p className="mt-2.5 text-[11px] md:text-xs text-foreground-600 leading-relaxed">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl flex items-center gap-2.5">
                <span className="w-1.5 h-4 rounded-full bg-secondary-500" />
                制作团队
              </h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {anime.staff.map((s) => (
                  <div key={s.role} className="p-4 rounded-lg bg-background-100 border border-background-200">
                    <p className="text-[11px] text-foreground-500 font-label">{s.role}</p>
                    <p className="mt-1 text-sm font-medium text-foreground-950">{s.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <figure className="mt-8">
              <button
                type="button"
                onClick={() => setPreview(anime.banner)}
                className="block w-full rounded-lg overflow-hidden bg-background-200 cursor-zoom-in group"
                aria-label="放大查看作品场景图"
              >
                <img
                  src={anime.banner}
                  alt={`${anime.title} 作品场景`}
                  title={`${anime.title} 作品场景图`}
                  className="w-full aspect-video object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.02]"
                />
              </button>
              <figcaption className="mt-2 text-[11px] text-foreground-500 flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-zoom-in-line" />
                </span>
                点击图片可放大预览 · 作品图仅供介绍展示
              </figcaption>
            </figure>

            <div className="mt-8 p-4 md:p-5 rounded-lg bg-accent-50 border border-accent-200">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center shrink-0">
                  <i className="ri-information-line text-base" />
                </span>
                <p className="text-xs md:text-sm text-foreground-700 leading-relaxed">
                  本站仅提供动漫作品信息与推荐，不提供在线播放或下载。评分与热度为演示数据，版权归原作品权利方所有。
                </p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">所属分类：</span>
                <Link
                  to="/anime"
                  className="px-3 h-8 inline-flex items-center rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                >
                  动漫作品
                </Link>
                {anime.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/anime?genre=${encodeURIComponent(g)}`}
                    className="px-3 h-8 inline-flex items-center rounded-full bg-primary-100 text-primary-700 text-xs hover:bg-primary-200 transition-colors duration-200 cursor-pointer"
                  >
                    {g}
                  </Link>
                ))}
              </div>
              <div className="flex gap-2">
                <Link
                  to="/anime"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-xs md:text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回列表
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-foreground-950 text-background-50 text-xs md:text-sm hover:bg-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line" />
                  首页
                </Link>
              </div>
            </div>
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
              <h2 className="font-heading font-semibold text-foreground-950 text-sm flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                  <i className="ri-newspaper-line text-base" />
                </span>
                相关资讯
              </h2>
              {relatedNews.length > 0 ? (
                <ul className="mt-3 space-y-3">
                  {relatedNews.map((n) => (
                    <li key={n.id}>
                      <Link to={`/news/detail/${n.id}`} className="flex gap-3 group cursor-pointer">
                        <div className="w-16 h-12 shrink-0 rounded-md overflow-hidden bg-background-200">
                          <img
                            src={n.cover}
                            alt={n.title}
                            title={`${n.title} 相关资讯`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs md:text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                            {n.title}
                          </p>
                          <p className="mt-1 text-[11px] text-foreground-400 font-label">{formatDate(n.publishedAt)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-xs text-foreground-500 leading-relaxed">暂无与该作品相关的资讯。</p>
              )}
            </div>

            <div className="rounded-lg bg-foreground-950 p-5">
              <h2 className="font-heading font-semibold text-background-50 text-sm flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                  <i className="ri-bar-chart-box-line text-base" />
                </span>
                数据一览
              </h2>
              <ul className="mt-3 space-y-2.5">
                {[
                  { label: "综合评分", value: `${anime.rating.toFixed(1)} / 10` },
                  { label: "讨论热度", value: formatCount(anime.heat) },
                  { label: "集数", value: anime.episodes },
                  { label: "状态", value: anime.status },
                ].map((row) => (
                  <li key={row.label} className="flex items-center justify-between text-xs">
                    <span className="text-background-400">{row.label}</span>
                    <span className="text-background-100 font-label">{row.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100 border-t border-background-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-bold text-foreground-950 text-lg md:text-xl">相关推荐作品</h2>
          <Link
            to="/anime"
            className="inline-flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
          >
            更多作品
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5" data-product-shop>
          {related.map((item, idx) => (
            <AnimeCard key={item.id} anime={item} index={idx} />
          ))}
        </div>
      </section>

      <ImageLightbox src={preview} alt={anime.title} onClose={() => setPreview(null)} />
    </SiteLayout>
  );
}